// CarrierResponsesTable.jsx
// -----------------------------------------------------------------------------
// - Renders a summary of carrier responses for a Tender.
// - Spot Tender:
//     * Simple vertical list of carriers with a checkbox (has quote?) and an
//       eye icon to open the Carrier Detail view.
// - Non-Spot Tender:
//     * Table layout where rows are carriers and columns are phases,
//       with checkboxes indicating whether each carrier quoted each phase.
// - The component is responsive; styles and horizontal scroll are handled
//   in CarrierResponsesTable.css.
// -----------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import { MCheckBox } from "src/M_Components";
import { useTranslation } from "react-i18next";
import "./CarrierResponsesTable.css";

export default function CarrierResponsesTable({
  tenderId, // Not used directly here, kept for potential future needs
  phases = [], // Array of phases (Non-Spot tenders only)
  isSpotTender, // Flag indicating Spot vs Non-Spot tender
  setActiveView, // Setter to switch between "inQuotation" and "carrier" views
  setSelectedCarrierId, // Setter to choose which carrier is being inspected
  responses = [], // Array of quotes/responses
  carriers = [], // Array of carrier objects
}) {
  const { t } = useTranslation();

  // Treats inactive quotes as non-existent so the UI matches the rest of the page filters
  const isActiveResponse = (r) => {
    const inactive = r?.Inactive ?? r?.inactive;
    return inactive === undefined || inactive === false;
  };

  // ==========================================================================
  // Spot Tender Layout
  // - Simple list of carriers. Each row shows:
  //     * Carrier name
  //     * Checkbox indicating if the carrier has *any* quote
  //     * Eye icon to open the carrier detail view (only if has quote)
  // ==========================================================================
  if (isSpotTender) {
    return (
      <div className="crt-root">
        <span className="crt-title">{t("Carrier Responses")}</span>

        <div className="crt-spot-list">
          {carriers.map((carrier) => {
            // Normalize ids/names from possible different payload shapes
            const carrierId = carrier.CarrierId || carrier.Id;
            const carrierName = carrier.Name || carrier.CarrierName;

            // Uses only active quotes to decide whether the carrier responded
            const hasQuote = responses.some((r) => {
              if (!isActiveResponse(r)) return false;
              const respCarrierId = r.CarrierId ?? r.carrierId;
              return respCarrierId === carrierId;
            });

            const eyeDisabled = !hasQuote;

            return (
              <div key={carrierId} className="crt-spot-row">
                <span className="crt-spot-carrier-name">{carrierName}</span>

                {/* Checkbox is only an indicator (always disabled) */}
                <MCheckBox checked={hasQuote} disabled />

                {/* Eye icon to open carrier detail view */}
                <i
                  className={`pi pi-eye crt-eye-icon ${
                    eyeDisabled ? "crt-eye-icon--disabled" : ""
                  }`}
                  onClick={() => {
                    if (!hasQuote) return;
                    setSelectedCarrierId(carrierId);
                    setActiveView("carrier");
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ==========================================================================
  // Non-Spot Tender Layout
  // - Table where:
  //     * Each row is a carrier.
  //     * Each column (except the last) is a Phase.
  //     * Checkboxes indicate if the carrier has a quote for that phase.
  //     * Last column has an eye icon to open the detail view if any quote exists.
  // - Table is horizontally scrollable if it does not fit in the container.
  // ==========================================================================
  return (
    <div className="crt-root crt-root--nonspot">
      <span className="crt-title">{t("Carrier Responses")}</span>

      {/* Wrapper adds horizontal scroll if the table grows beyond the container width */}
      <div className="crt-table-wrapper">
        <table className="crt-table">
          <thead>
            <tr className="crt-header-row">
              <th className="crt-th crt-th-carrier">{t("Carrier")}</th>

              {phases.map((phase) => (
                <th key={phase.Id} className="crt-th crt-th-phase">
                  {phase.Name}
                </th>
              ))}

              {/* Actions column (eye icon) */}
              <th className="crt-th crt-th-actions" />
            </tr>
          </thead>

          <tbody>
            {carriers.map((carrier) => {
              const carrierId = carrier.CarrierId || carrier.Id;
              const carrierName = carrier.Name || carrier.CarrierName;

              // Checks if this carrier has an active quote for a specific phase
              const hasPhaseQuote = (phaseId) =>
                responses.some((r) => {
                  if (!isActiveResponse(r)) return false;
                  const respCarrierId = r.CarrierId ?? r.carrierId;
                  const respPhaseId = r.TenderPhaseId ?? r.tenderPhaseId;
                  return respCarrierId === carrierId && respPhaseId === phaseId;
                });

              // Checks if the carrier has at least one active quote in any phase
              const hasAnyQuote = phases.some((p) => hasPhaseQuote(p.Id));

              const eyeDisabled = !hasAnyQuote;

              return (
                <tr key={carrierId} className="crt-row">
                  {/* Carrier name */}
                  <td className="crt-td crt-td-carrier">{carrierName}</td>

                  {/* One checkbox per phase (active quote exists or not) */}
                  {phases.map((phase) => (
                    <td
                      key={phase.Id}
                      className="crt-td crt-td-center crt-td-phase"
                    >
                      <MCheckBox checked={hasPhaseQuote(phase.Id)} disabled />
                    </td>
                  ))}

                  {/* Eye icon to open carrier detail view */}
                  <td className="crt-td crt-td-center crt-td-actions">
                    <i
                      className={`pi pi-eye crt-eye-icon ${
                        eyeDisabled ? "crt-eye-icon--disabled" : ""
                      }`}
                      onClick={() => {
                        if (!hasAnyQuote) return;
                        setSelectedCarrierId(carrierId);
                        setActiveView("carrier");
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

CarrierResponsesTable.propTypes = {
  tenderId: PropTypes.number,
  phases: PropTypes.array,
  isSpotTender: PropTypes.bool,
  setActiveView: PropTypes.func,
  setSelectedCarrierId: PropTypes.func,
  responses: PropTypes.array,
  carriers: PropTypes.array,
};
